import React, { useEffect, useState } from 'react';
import { Briefcase, Edit, Plus, RefreshCw, Trash2, Search, Globe, Settings, FileText, Target, Share2, Twitter, Facebook, MessageCircle, Link as LinkIcon, X, ClipboardList } from 'lucide-react';
import { API_URL, PORTAIL_URL } from '../config/api';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import http from '../api/http';

const STATUTS = [
  { value: 'brouillon', label: 'Brouillon', className: 'bg-gray-100 text-gray-700' },
  { value: 'publiee', label: 'Publiée', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'fermee', label: 'Fermée', className: 'bg-amber-100 text-amber-700' },
  { value: 'pourvue', label: 'Pourvue', className: 'bg-sky-100 text-sky-700' },
];

const emptyForm = {
  titre: '',
  reference: '',
  description: '',
  missions: '',
  profil_recherche: '',
  avantages: '',
  lieu: '',
  mode_travail: 'site',
  type_contrat: 'CDI',
  type_offre: 'externe',
  experience_min: '',
  experience_max: '',
  salaire_min: '',
  salaire_max: '',
  devise: 'XOF',
  departement_id: '',
  titre_id: '',
  date_publication: '',
  date_cloture: '',
  statut: 'brouillon',
  nb_postes: 1,
};

const Offres = () => {
  const { filialeId } = useFiliale();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState(emptyForm);
  const [filters, setFilters] = useState({ statut: '', q: '' });
  const [departements, setDepartements] = useState([]);
  const [titres, setTitres] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedOffre, setSelectedOffre] = useState(null);

  const tabs = [
    { id: 'general', label: 'Général', icon: Settings },
    { id: 'description', label: 'Description', icon: FileText },
    { id: 'conditions', label: 'Conditions', icon: Target }
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filialeId && filialeId !== 'all') params.filiale_id = filialeId;
      if (filters.statut) params.statut = filters.statut;
      if (filters.q) params.q = filters.q;
      const response = await http.get('/api/offres', { params });
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur offres:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefs = async () => {
    try {
      const params = filialeId && filialeId !== 'all' ? { filiale_id: filialeId } : {};
      const [deps, tits] = await Promise.all([
        http.get('/api/departements'),
        http.get('/api/titres', { params }),
      ]);
      setDepartements(Array.isArray(deps.data) ? deps.data : []);
      setTitres(Array.isArray(tits.data) ? tits.data : (tits.data?.data || []));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchData();
    fetchRefs();
  }, [filialeId]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ ...emptyForm, filiale_id: filialeId !== 'all' ? filialeId : '' });
    setActiveTab('general');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ...emptyForm,
      ...item,
      experience_min: item.experience_min ?? '',
      experience_max: item.experience_max ?? '',
      salaire_min: item.salaire_min ?? '',
      salaire_max: item.salaire_max ?? '',
      departement_id: item.departement_id ?? '',
      titre_id: item.titre_id ?? '',
      date_publication: item.date_publication ? String(item.date_publication).slice(0, 10) : '',
      date_cloture: item.date_cloture ? String(item.date_cloture).slice(0, 10) : '',
    });
    setActiveTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette offre et ses candidatures associées ?')) return;
    try {
      await http.delete(`/api/offres/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur suppression');
    }
  };

  const publishToggle = async (item) => {
    const next = item.statut === 'publiee' ? 'fermee' : 'publiee';
    try {
      await http.put(`/api/offres/${item.id}`, {
        statut: next,
        date_publication: next === 'publiee' && !item.date_publication
          ? new Date().toISOString().slice(0, 10)
          : item.date_publication,
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur publication');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        filiale_id: formData.filiale_id || (filialeId !== 'all' ? filialeId : undefined),
        experience_min: formData.experience_min === '' ? null : Number(formData.experience_min),
        experience_max: formData.experience_max === '' ? null : Number(formData.experience_max),
        salaire_min: formData.salaire_min === '' ? null : Number(formData.salaire_min),
        salaire_max: formData.salaire_max === '' ? null : Number(formData.salaire_max),
        departement_id: formData.departement_id || null,
        titre_id: formData.titre_id || null,
        date_publication: formData.date_publication || null,
        date_cloture: formData.date_cloture || null,
        nb_postes: Number(formData.nb_postes) || 1,
      };
      if (editingItem) {
        await http.put(`/api/offres/${editingItem.id}`, payload);
      } else {
        await http.post('/api/offres', payload);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur enregistrement');
    }
  };

  const statutBadge = (statut) => {
    const s = STATUTS.find((x) => x.value === statut) || STATUTS[0];
    return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${s.className}`}>{s.label}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Offres d'emploi"
        subtitle="Publication et suivi des postes ouverts"
        icon={Briefcase}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="px-6 py-8 space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                className="input-field pl-9 w-full"
                placeholder="Titre, lieu, référence…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchData()}
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Statut</label>
            <select
              className="input-field"
              value={filters.statut}
              onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
            >
              <option value="">Tous</option>
              {STATUTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <button type="button" onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Filtrer
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-500">Chargement…</div>
          ) : data.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">Aucune offre. Créez-en une pour démarrer.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filiale</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contrat</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidatures</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.titre}</div>
                        <div className="text-xs text-gray-500">{item.lieu || '—'} · {item.reference || `REF-${item.id}`}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.filiale_libelle || item.filiale_id}</td>
                      <td className="px-4 py-3">
                        {item.type_offre === 'interne' ? (
                          <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">Interne</span>
                        ) : item.type_offre === 'mixte' ? (
                          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Mixte</span>
                        ) : (
                          <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">Externe</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{item.type_contrat} · {item.mode_travail}</td>
                      <td className="px-4 py-3">{statutBadge(item.statut)}</td>
                      <td className="px-4 py-3">
                        {item.has_questionnaire ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                            <ClipboardList className="h-3 w-3" /> Requis
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Non</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          {item.nb_candidatures || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button type="button" title="Publier / Fermer" onClick={() => publishToggle(item)} className="text-emerald-600 hover:text-emerald-800">
                            <Globe className="h-4 w-4" />
                          </button>
                          <button type="button" title="Partager" onClick={() => { setSelectedOffre(item); setShowShareModal(true); }} className="text-purple-600 hover:text-purple-800">
                            <Share2 className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-bold text-blue-900">{editingItem ? 'Modifier' : 'Créer'} une offre</h3>
              <button type="button" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {/* Navigation par onglets */}
            <nav className="flex gap-1 overflow-x-auto border-b px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              {activeTab === 'general' && (
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium">Titre *</label>
                      <input className="input-field w-full" required value={formData.titre} onChange={(e) => setFormData({ ...formData, titre: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Référence</label>
                      <input className="input-field w-full" value={formData.reference || ''} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Lieu</label>
                      <input className="input-field w-full" value={formData.lieu || ''} onChange={(e) => setFormData({ ...formData, lieu: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Type de contrat</label>
                      <select className="input-field w-full" value={formData.type_contrat} onChange={(e) => setFormData({ ...formData, type_contrat: e.target.value })}>
                        {['CDI', 'CDD', 'Stage', 'Alternance', 'Freelance', 'Intérim'].map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Type d'offre</label>
                      <select className="input-field w-full" value={formData.type_offre} onChange={(e) => setFormData({ ...formData, type_offre: e.target.value })}>
                        <option value="externe">Externe (candidats externes)</option>
                        <option value="interne">Interne (employés internes)</option>
                        <option value="mixte">Mixte (interne + externe)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.type_offre === 'interne' ? 'Seuls les employés de la filiale seront notifiés' :
                         formData.type_offre === 'mixte' ? 'Employés internes et candidats externes' :
                         'Offre publique pour tous les candidats'}
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Mode de travail</label>
                      <select className="input-field w-full" value={formData.mode_travail} onChange={(e) => setFormData({ ...formData, mode_travail: e.target.value })}>
                        <option value="site">Sur site</option>
                        <option value="hybride">Hybride</option>
                        <option value="teletravail">Télétravail</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Statut</label>
                      <select className="input-field w-full" value={formData.statut} onChange={(e) => setFormData({ ...formData, statut: e.target.value })}>
                        {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Nb postes</label>
                      <input type="number" min="1" className="input-field w-full" value={formData.nb_postes} onChange={(e) => setFormData({ ...formData, nb_postes: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Département</label>
                      <select className="input-field w-full" value={formData.departement_id || ''} onChange={(e) => setFormData({ ...formData, departement_id: e.target.value })}>
                        <option value="">—</option>
                        {departements.map((d) => <option key={d.iddepartement} value={d.iddepartement}>{d.libelle_departement}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Titre / Poste</label>
                      <select className="input-field w-full" value={formData.titre_id || ''} onChange={(e) => setFormData({ ...formData, titre_id: e.target.value })}>
                        <option value="">—</option>
                        {titres.map((t) => <option key={t.idtitre} value={t.idtitre}>{t.nom}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Publication</label>
                      <input type="date" className="input-field w-full" value={formData.date_publication || ''} onChange={(e) => setFormData({ ...formData, date_publication: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Clôture</label>
                      <input type="date" className="input-field w-full" value={formData.date_cloture || ''} onChange={(e) => setFormData({ ...formData, date_cloture: e.target.value })} />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'description' && (
                <div className="p-6 space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Description</label>
                    <textarea rows={5} className="input-field w-full" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Décrivez le poste et l'entreprise..." />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Missions</label>
                    <textarea rows={5} className="input-field w-full" value={formData.missions || ''} onChange={(e) => setFormData({ ...formData, missions: e.target.value })} placeholder="Listez les principales missions et responsabilités..." />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Profil recherché</label>
                    <textarea rows={5} className="input-field w-full" value={formData.profil_recherche || ''} onChange={(e) => setFormData({ ...formData, profil_recherche: e.target.value })} placeholder="Décrivez le profil idéal du candidat..." />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Avantages</label>
                    <textarea rows={3} className="input-field w-full" value={formData.avantages || ''} onChange={(e) => setFormData({ ...formData, avantages: e.target.value })} placeholder="Avantages sociaux, avantages en nature..." />
                  </div>
                </div>
              )}

              {activeTab === 'conditions' && (
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium">Expérience min (années)</label>
                      <input type="number" min="0" className="input-field w-full" value={formData.experience_min} onChange={(e) => setFormData({ ...formData, experience_min: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Expérience max (années)</label>
                      <input type="number" min="0" className="input-field w-full" value={formData.experience_max} onChange={(e) => setFormData({ ...formData, experience_max: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Salaire min</label>
                      <input type="number" min="0" className="input-field w-full" value={formData.salaire_min} onChange={(e) => setFormData({ ...formData, salaire_min: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Salaire max</label>
                      <input type="number" min="0" className="input-field w-full" value={formData.salaire_max} onChange={(e) => setFormData({ ...formData, salaire_max: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Devise</label>
                      <select className="input-field w-full" value={formData.devise || 'XOF'} onChange={(e) => setFormData({ ...formData, devise: e.target.value })}>
                        <option value="XOF">XOF (FCFA)</option>
                        <option value="EUR">EUR (Euro)</option>
                        <option value="USD">USD (Dollar)</option>
                        <option value="CAD">CAD (Dollar canadien)</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 border-t px-6 py-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-200 rounded-md">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editingItem ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Partage Social */}
      {showShareModal && selectedOffre && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            {/* Header avec gradient */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Share2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Partager l'offre</h3>
                    <p className="text-purple-100 text-sm">{selectedOffre.titre}</p>
                  </div>
                </div>
                <button type="button" onClick={() => setShowShareModal(false)} className="text-white/80 hover:text-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Boutons de partage */}
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Nouvelle opportunité : ${selectedOffre.titre} chez Media Contact Côte d'Ivoire. Postulez maintenant !`)}&url=${encodeURIComponent(PORTAIL_URL)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-[#1da1f2] hover:bg-[#0d8bd9] text-white rounded-xl transition-all hover:scale-105"
                >
                  <Twitter className="h-5 w-5" />
                  <span className="font-medium">Twitter</span>
                </a>

                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(PORTAIL_URL)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-[#1877f2] hover:bg-[#0d65d9] text-white rounded-xl transition-all hover:scale-105"
                >
                  <Facebook className="h-5 w-5" />
                  <span className="font-medium">Facebook</span>
                </a>

                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Nouvelle opportunité : ${selectedOffre.titre}. Postulez ici : ${PORTAIL_URL}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-[#25d366] hover:bg-[#128c7e] text-white rounded-xl transition-all hover:scale-105"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span className="font-medium">WhatsApp</span>
                </a>
              </div>

              {/* Copier le lien */}
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Lien du portail</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={PORTAIL_URL}
                    className="input-field flex-1 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(PORTAIL_URL);
                      alert('Lien copié !');
                    }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
                    title="Copier le lien"
                  >
                    <LinkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Message pré-rempli */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Message de partage (Twitter)</label>
                <p className="text-sm text-gray-600 italic">
                  "Nouvelle opportunité : {selectedOffre.titre} chez Media Contact Côte d'Ivoire. Postulez maintenant !"
                </p>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t">
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl transition-colors font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offres;
